<template>
  <div>
    <alarm-header
      ref="header"
      :loading="loading.header"
      :filter="filter"
      :complete-filter="completeFilter"
      @search="onSearchButtonClick"
      @on-interval="$event.wait(onRefreshTimer())"
    />
    <alarm-subtitle-modal/>
    <app-content>
      <b-row>
        <b-col
          cols="12"
          lg="6"
        >
          <data-panel
            class="mb-2"
            :title="$t('general.general')"
            :value="generalData"
            :loading="loading.content"
            :filter="filter"
            ref="accordion1"
            @collapse-change="changeCollapse($event, generalData)"
            @expand-all="collapseAll($event, generalData)"
            @collapse-all="collapseAll($event, generalData)"
            @recognize-alarm="onRecognizeAlarm"
          />
        </b-col>
        <b-col
          cols="12"
          lg="6"
        >
          <data-panel
            class="mb-2"
            :title="$t('fields.alarm.alarmed')"
            :value="alarmedData"
            :loading="loading.content"
            :filter="{ ...filter, generalFilter:
              { ...filter.generalFilter, selectedAlarmStatus: getAlarmedFilterFromSelection() } }"
            ref="accordion2"
            @collapse-change="changeCollapse($event, alarmedData)"
            @expand-all="collapseAll($event, alarmedData)"
            @collapse-all="collapseAll($event, alarmedData)"
            @recognize-alarm="onRecognizeAlarm"
          />
        </b-col>
      </b-row>
    </app-content>
  </div>
</template>

<script>
import AlarmsAndWarningService from '@/domain/services/generalPanel/alarmsAndWarnings/general';
import DataPanel from '@/domain/components/generalPanel/alarmsAndWarnings/DataPanel';
import AlarmSubtitleModal from '@/domain/components/generalPanel/alarmsAndWarnings/AlarmSubtitleModal';
import localEventBus, {
  events,
} from '@/domain/services/generalPanel/alarmsAndWarnings/localEventBus';
import CompanyService from '@/domain/services/company';
import RegionalService from '@/domain/services/regional';
import InstallationService from '@/domain/services/installation';
import AssetService from '@/domain/services/asset';
import storageManager from '@/services/sessionStorageManager';
import periods from '@/domain/enums/periods-dropdown-filter';
import { alarmsAndWarningsStatusLookup, alarmsAndWarningsStatusLookupAlarmed }
  from '@/domain/data/generalPanel/reports/alarmsAndWarnings/alarmsAndWarningsStatusLookup';
import alarmsClassificationsLookup
  from '@/domain/data/generalPanel/reports/alarmsAndWarnings/alarmsClassificationsLookup';
import installationStatusLookup
  from '@/domain/data/generalPanel/reports/alarmsAndWarnings/alarmsInstallationsStatusLookup';
import AlarmHeader from '@/domain/components/generalPanel/alarmsAndWarnings/AlarmHeader';

export default {
  name: 'AlarmsAndWarnings',
  components: {
    DataPanel,
    AlarmHeader,
    AlarmSubtitleModal,
  },
  data: vm => ({
    generalData: [],
    alarmedData: [],
    wasRedirected: false,
    scrollLocation: 0,
    modalPromise: null,
    loading: {
      header: false,
      content: false,
    },
    filter: {
      companyFilter: {
        companies: [],
        regional: [],
        installations: [],
        assets: [],
      },
      generalFilter: {
        selectedAlarmStatus: alarmsAndWarningsStatusLookup(vm),
        selectedAlarmClassifications: alarmsClassificationsLookup(vm),
        selectedInstallationStatus: installationStatusLookup(vm),
        period: periods.ALL,
      },
    },
    completeFilter: {
      companyFilter: {
        companies: [],
        regional: [],
        installations: [],
        assets: [],
      },
      generalFilter: {
        selectedAlarmStatus: alarmsAndWarningsStatusLookup(vm),
        selectedAlarmClassifications: alarmsClassificationsLookup(vm),
        selectedInstallationStatus: installationStatusLookup(vm),
        period: periods.ALL,
      },
    },
    alarmedFilter: {
      selectedAlarmStatus: alarmsAndWarningsStatusLookupAlarmed(vm),
    },
  }),
  computed: {
    companyFilter() {
      return this.filter.companyFilter;
    },
    generalFilter() {
      return this.filter.generalFilter;
    },
    requestFilter() {
      return {
        companiesIds: [...this.companyFilter.companies.map(x => x.id)],
        regionalIds: [...this.companyFilter.regional.map(x => x.id)],
        installationsIds: [...this.companyFilter.installations.map(x => x.id)],
        assetsIds: [...this.companyFilter.assets.map(x => x.id)],
        alarmStatus: [...this.generalFilter.selectedAlarmStatus.map(x => x.id)],
        classifications: [...this.generalFilter.selectedAlarmClassifications.map(x => x.id)],
        installationStatus: [...this.generalFilter.selectedInstallationStatus.map(x => x.id)],
        period: this.generalFilter.period,
      };
    },
  },
  mounted() {
    this.init();
  },
  beforeDestroy() {
    this.deinit();
  },
  methods: {
    async loadAllFilters() {
      this.loading.header = true;
      const rawStorage = storageManager.getItem(this.$route.query?.filter);
      const storageFilter = this.tryJsonParse(rawStorage);
      this.wasRedirected = storageFilter?.redirected;
      await this.loadCompanies(storageFilter);
      await this.loadRegionals(storageFilter);
      await this.loadInstallations(storageFilter);
      await this.loadAssets(storageFilter);
      this.applyGeneralCacheFilter(storageFilter);
      this.loading.header = false;
    },
    async loadCompanies(storageFilter) {
      await CompanyService.getCompanies().then((response) => {
        const companiesInStorage = storageFilter?.companiesIds;

        this.completeFilter.companyFilter.companies = response || [];

        if (companiesInStorage?.length) {
          this.companyFilter.companies = this.filterByListWithNormalizedIds(
            response,
            companiesInStorage,
          );
          return;
        }

        this.companyFilter.companies = response || [];
      });
    },
    async loadRegionals(storageFilter) {
      if (!this.companyFilter.companies.length) {
        return;
      }

      const companiesIds = this.completeFilter.companyFilter.companies.map(x => x.id);
      await RegionalService.getRegional(companiesIds).then((response) => {
        const regionalsInStorage = storageFilter?.regionalIds;

        this.completeFilter.companyFilter.regional = response || [];

        if (regionalsInStorage?.length) {
          this.companyFilter.regional = this.filterByListWithNormalizedIds(
            response,
            regionalsInStorage,
          );
          return;
        }

        this.companyFilter.regional = response || [];
      });
    },
    async loadInstallations(storageFilter) {
      if (!this.companyFilter.companies.length) {
        return;
      }

      const params = {
        companiesIds: this.completeFilter.companyFilter.companies.map(x => x.id),
        regionalIds: (this.completeFilter.companyFilter.regional || []).map(x => x.id),
      };
      await InstallationService.getInstallationsByCompaniesOrRegional(params).then((response) => {
        const installationsInStorage = storageFilter?.installationsIds;

        this.completeFilter.companyFilter.installations = response || [];

        if (installationsInStorage?.length) {
          this.companyFilter.installations = this.filterByListWithNormalizedIds(
            response,
            installationsInStorage,
          );
          return;
        }

        this.companyFilter.installations = response || [];
      });
    },
    async loadAssets(storageFilter) {
      if (!this.companyFilter.installations.length) {
        return;
      }

      const params = {
        installationsIds: this.completeFilter.companyFilter.installations.map(x => x.id),
      };
      await AssetService.getAssetsByInstallations(params).then((response) => {
        const assetsInStorage = storageFilter?.assetsIds;
        const mappedResponse = (response || []).map(item => ({
          id: item.id,
          name: `${item.installation?.name} | ${item.name}`,
          installation: item.installation,
        }));

        this.completeFilter.companyFilter.assets = mappedResponse || [];

        if (assetsInStorage?.length) {
          this.companyFilter.assets = this.filterByListWithNormalizedIds(
            mappedResponse,
            assetsInStorage,
          );
          return;
        }

        this.companyFilter.assets = mappedResponse || [];
      });
    },
    tryJsonParse(item) {
      try {
        return JSON.parse(item);
      } catch (e) {
        return {};
      }
    },
    applyGeneralCacheFilter(storageFilter) {
      if (!storageFilter) {
        return;
      }

      const completeFilter = this.completeFilter.generalFilter;

      const classificationIds = (storageFilter.classifications || [])
        .map(classification => ({ id: classification }));

      this.generalFilter.selectedAlarmClassifications = this.filterByList(
        completeFilter.selectedAlarmClassifications,
        classificationIds,
      );

      const alarmStatusIds = (storageFilter.alarmStatus || [])
        .map(alarmStatus => ({ id: alarmStatus }));

      this.generalFilter.selectedAlarmStatus = this.filterByList(
        completeFilter.selectedAlarmStatus,
        alarmStatusIds,
      );

      const installationStatusIds = (storageFilter.installationStatus || [])
        .map(installationStatus => ({ id: installationStatus }));

      this.generalFilter.selectedInstallationStatus = this.filterByList(
        completeFilter.selectedInstallationStatus,
        installationStatusIds,
      );

      this.generalFilter.period = storageFilter.period || completeFilter.period;
    },
    filterByList(list, filterList) {
      if (filterList?.length) {
        const filterListIds = filterList.map(x => x.id);
        return list.filter(x => filterListIds.includes(x.id));
      }

      return list;
    },
    filterByListWithNormalizedIds(list, filterList) {
      if (filterList?.length) {
        const upperIds = filterList.map(x => (x || '').toUpperCase());
        return list.filter(x => upperIds.includes((x.id || '').toUpperCase()));
      }

      return list;
    },
    onSearchButtonClick(filter) {
      if (this.loading.content) {
        return;
      }

      this.filter = filter;
      // this.setCacheData(); TODO: Ajustar na feature #42494 junto com a tela do dash
      this.$refs.header.hideFilters();
      this.refreshData();
    },
    refreshData() {
      this.loading.content = true;
      return AlarmsAndWarningService.getAlarmsByCompany(this.requestFilter)
        .then((response) => {
          this.generalData = this.mapWithCollapse(response, this.generalData);
          this.alarmedData = this.filterAlarmedInstallations(this.filterAlarmedCompanies(response));
        }).finally(() => {
          this.loading.content = false;
          this.goToScrollLocation();
        });
    },
    setCacheData() {
      const cacheId = this.$route.query?.filter;

      if (cacheId) {
        storageManager.replaceItemWithId(cacheId, this.requestFilter);
        return;
      }

      const stringifyFilter = JSON.stringify(this.requestFilter);
      const newCacheId = storageManager.setItemWithId('alarmAndWarningFilter', stringifyFilter);
      this.$router.push({ query: { filter: newCacheId } });
    },
    getCollapseByCompanyFromValues(responseCompany, values) {
      const value = values.find(
        currentCompany => responseCompany.companyId === currentCompany.companyId,
      );
      return (value || {}).collapse;
    },
    mapWithCollapse(response, values) {
      return response.map(company => ({
        ...company,
        collapse: this.getCollapseByCompanyFromValues(company, values),
      }));
    },
    filterAlarmedCompanies(response) {
      const companies = this.mapWithCollapse(response, this.alarmedData);
      return companies.filter((company) => {
        const companyAlarms = (company.summary || {}).alarms;
        if (!companyAlarms) return false;
        return companyAlarms.actedAndNotRecognized > 0 || companyAlarms.actedAndRecognized > 0;
      });
    },
    filterAlarmedInstallations(companies) {
      return companies.map((company) => {
        const newCompany = { ...company };
        newCompany.regional = newCompany.regional.map((regional) => {
          const installationsFiltered = regional.installations.filter(
            installation => installation.alarmsSummary.actedAndNotRecognized > 0
            || installation.alarmsSummary.actedAndRecognized > 0,
          );
          return { ...regional, installations: installationsFiltered };
        });
        return newCompany;
      });
    },
    async init() {
      localEventBus.on(events.MODAL_OPEN, this.onModalOpen, this);
      localEventBus.on(events.END_GET_DESCRIPTION, this.onEndGetDescription, this);
      localEventBus.on(events.SET_USER_POSITION, this.setUserScrollLocation, this);
      await this.loadAllFilters();
      await this.refreshData();
      this.openAccordionsWhenRedirected();
    },
    openAccordionsWhenRedirected() {
      if (!this.wasRedirected) {
        return;
      }
      this.$nextTick(() => {
        this.$refs.accordion1.onCollapseExpandDropdownClick(true);
        this.$refs.accordion2.onCollapseExpandDropdownClick(true);
      });
    },
    deinit() {
      localEventBus.off(events.MODAL_OPEN, this.onModalOpen, this);
      localEventBus.off(events.END_GET_DESCRIPTION, this.onEndGetDescription, this);
      localEventBus.off(events.SET_USER_POSITION, this.setUserScrollLocation, this);
    },
    onRecognizeAlarm() {
      if (this.loading.content) return;
      this.setUserScrollLocation();
      this.refreshData();
    },
    onEndGetDescription() {
      this.goToScrollLocation();
    },
    onModalOpen(waiter) {
      this.modalPromise = waiter.toPromise()
        .catch(() => null)
        .finally(() => {
          this.modalPromise = null;
        });
    },
    waitModals() {
      if (this.modalPromise) {
        return this.modalPromise;
      }
      return Promise.resolve();
    },
    async onRefreshTimer() {
      if (this.loading.content) return;
      this.setUserScrollLocation();
      await this.waitModals();
      this.refreshData();
    },
    setUserScrollLocation() {
      const view = document.getElementsByClassName('app__content-wrapper')[0];
      if (!view) return;
      this.scrollLocation = view.scrollTop;
    },
    goToScrollLocation() {
      this.$nextTick(() => {
        const view = document.getElementsByClassName('app__content-wrapper')[0];
        if (!view) return;
        view.scrollTo(0, this.scrollLocation);
      });
    },
    changeCollapse(options, values) {
      const company = values.find(data => data.companyId === options.id);
      this.$set(company, 'collapse', options.value);
    },
    collapseAll(collapse, values) {
      values.forEach((data) => {
        this.changeCollapse({ id: data.companyId, value: collapse }, values);
      });
    },
    getAlarmedFilterFromSelection() {
      const actedStatusIds = [
        'acted-not-recognized',
        'acted-recognized',
      ];

      return this.generalFilter.selectedAlarmStatus.filter(
        alarm => actedStatusIds.includes(alarm.id),
      );
    },
  },
};
</script>
